import React, { useState, useEffect, useRef } from 'react';
import AdminLayout from './AdminLayout';
import { useNotification } from '../../components/framework/NotificationContext';
import ReviewImage from '../common/ReviewImage';
import RecordDetailView from './RecordDetailView';
import { loadAllInBatches } from '../../utils/loadAllInBatches';

const BATCH_SIZE = 25;

const STATUS_FILTER_LABELS = {
    PENDING: 'Pending',
    APPROVED: 'Approved',
    REJECTED: 'Rejected',
    NEEDS_UPDATE: 'Needs Update',
};

/**
 * SubmissionManager
 *
 * A unified component for managing and moderating submissions.
 * Used by both "Manage Entries" (Admin) and "Moderation Queue" (Moderator/Admin).
 *
 * @param {string} title - Page title
 * @param {string} subtitle - Page subtitle
 * @param {Function} fetchData - Async function(page, size) returning a Spring Page of experiments. Loaded in batches: the first batch renders immediately, the rest streams in the background.
 * @param {Function} fetchStats - Async function to fetch stats (optional)
 * @param {Function} onAction - Async function(id, action, currentStatus) to handle single row action
 * @param {Function} onBulkAction - Async function(ids, action) to handle bulk action (optional)
 * @param {Function} onDelete - Async function(id) to permanently delete a record (optional)
 * @param {boolean} allowStatusFilter - Whether to show the status dropdown filter
 * @param {Array} statusFilterOptions - Statuses the dropdown offers besides "All" (default: every status)
 * @param {boolean} allowBulkActions - Whether to show bulk action buttons
 * @param {boolean} allowDelete - Whether to show the permanent delete action per row
 * @param {Array} allowedActions - List of allowed actions per row ['APPROVE', 'REJECT', 'PENDING']
 * @param {Object} statusMapping - Maps internal status to display badge/color
 */
const SubmissionManager = ({
    title,
    subtitle,
    fetchData,
    fetchStats,
    onAction,
    onBulkAction,
    onDelete,
    allowStatusFilter = false,
    statusFilterOptions = Object.keys(STATUS_FILTER_LABELS),
    allowBulkActions = false,
    allowDelete = false,
    allowedActions = ['APPROVED', 'REJECTED', 'PENDING'],
    emptyMessage = "No entries found."
}) => {
    const [experiments, setExperiments] = useState([]);
    const [filteredExperiments, setFilteredExperiments] = useState([]);
    const [filterStatus, setFilterStatus] = useState('ALL');
    const [searchTerm, setSearchTerm] = useState('');
    const [sortOrder, setSortOrder] = useState('newest'); // newest | completeness-asc | completeness-desc
    const [expandedId, setExpandedId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0, needsUpdate: 0 });
    const [dataLoaded, setDataLoaded] = useState(false);

    // Background batch-loading progress. allLoaded gates bulk actions so
    // "Approve/Reject All Visible" never silently applies to a partial set.
    const [allLoaded, setAllLoaded] = useState(false);
    const [loadProgress, setLoadProgress] = useState({ loaded: 0, total: 0 });
    const loadTokenRef = useRef(0);

    // Notifications
    const { success, error: showError } = useNotification();

    // Confirmation Modal State
    const [confirmModal, setConfirmModal] = useState({
        isOpen: false,
        action: '',
        displayAction: '',
        expectedPhrase: '',
        count: 0
    });
    const [confirmInput, setConfirmInput] = useState('');

    // Note to the contributor, asked when a run is rejected or sent back for an update
    const [noteModal, setNoteModal] = useState(null); // { id, action, text }

    // Permanent Delete Modal State
    const [deleteModal, setDeleteModal] = useState({ isOpen: false, recordId: null });
    const [deleteConfirmInput, setDeleteConfirmInput] = useState('');

    // Load Stats on Mount
    useEffect(() => {
        if (fetchStats) loadStats();
    }, []);

    // Invalidate any in-flight background batch loading on unmount.
    useEffect(() => {
        return () => { loadTokenRef.current += 1; };
    }, []);

    const loadStats = async () => {
        try {
            const data = await fetchStats();
            if (data) setStats(data);
        } catch (err) {
            console.error("Failed to load stats", err);
        }
    };

    // Filter Logic
    useEffect(() => {
        let filtered = experiments;

        // Status Filter
        if (allowStatusFilter && filterStatus !== 'ALL') {
            filtered = filtered.filter(exp => exp.status === filterStatus);
        }

        // Search Filter
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(exp => {
                const polymerNames = exp.polymerProperty?.polymerComponents?.map(c => c.polymerName.toLowerCase()).join(' ') || '';
                const email = exp.userInfo?.email?.toLowerCase() || '';
                const name = exp.userInfo?.name?.toLowerCase() || '';
                const id = exp.recordId?.toString() || '';
                return polymerNames.includes(term) || email.includes(term) || name.includes(term) || id.includes(term);
            });
        }

        // Completeness sort: records without a score go last either way
        if (sortOrder !== 'newest') {
            const dir = sortOrder === 'completeness-asc' ? 1 : -1;
            filtered = [...filtered].sort((a, b) => {
                const sa = a.completenessScore, sb = b.completenessScore;
                if (sa == null && sb == null) return 0;
                if (sa == null) return 1;
                if (sb == null) return -1;
                return dir * (sa - sb) || b.recordId - a.recordId;
            });
        }

        setFilteredExperiments(filtered);
    }, [filterStatus, experiments, searchTerm, allowStatusFilter, sortOrder]);

    // Update stats locally when data changes if not using external fetchStats
    useEffect(() => {
        if (dataLoaded && !fetchStats) {
            setStats({
                total: experiments.length,
                pending: experiments.filter(e => e.status === 'PENDING').length,
                approved: experiments.filter(e => e.status === 'APPROVED').length,
                rejected: experiments.filter(e => e.status === 'REJECTED').length,
                needsUpdate: experiments.filter(e => e.status === 'NEEDS_UPDATE').length,
            });
        }
    }, [experiments, dataLoaded, fetchStats]);

    // Renders the first batch immediately, then streams the rest in the
    // background so bulk "All Visible" actions (gated by allLoaded) still see
    // the complete dataset once loading finishes.
    async function loadExperiments() {
        const myToken = ++loadTokenRef.current;
        setLoading(true);
        setError(null);
        setExperiments([]);
        setAllLoaded(false);
        setLoadProgress({ loaded: 0, total: 0 });

        let loadedCount = 0;

        try {
            await loadAllInBatches(
                (page, size) => fetchData(page, size),
                {
                    batchSize: BATCH_SIZE,
                    shouldContinue: () => loadTokenRef.current === myToken,
                    onBatch: (batch, meta) => {
                        if (loadTokenRef.current !== myToken) return;
                        loadedCount += batch.length;
                        setExperiments(prev => [...prev, ...batch]);
                        setLoadProgress({ loaded: loadedCount, total: meta.totalElements });
                        if (meta.isFirst) {
                            setDataLoaded(true);
                            setLoading(false);
                        }
                    }
                }
            );
            if (loadTokenRef.current === myToken) {
                setAllLoaded(true);
            }
        } catch (err) {
            if (loadTokenRef.current === myToken) {
                setError(err.message);
                setLoading(false);
            }
        }
    }

    const handleToggle = (id) => {
        setExpandedId(expandedId === id ? null : id);
    };

    const handleAction = async (id, action, note) => {
        // Guard against mutating a record while background batches are still
        // being appended to `experiments` - avoids racing the load.
        if (!allLoaded) {
            showError('Please wait for all records to finish loading before taking action.');
            return;
        }
        // The contributor sees the note in My contributions: ask for one first
        if (note === undefined && (action === 'REJECTED' || action === 'NEEDS_UPDATE')) {
            const current = experiments.find(e => e.recordId === id);
            setNoteModal({ id, action, text: current?.reviewNote || '' });
            return;
        }
        try {
            await onAction(id, action, note || '');

            // Optimistic Update
            // If the action is "approve" (moderation style), we typically remove it from the list if it's a queue
            // If it's "APPROVED" (manage style), we update the status.
            // We can check the action format to decide or let the parent handle the list update via reload.
            // But updating local state is smoother.

            // Normalize action for comparison
            const upperAction = action.toUpperCase();

            // If we are in a simple queue (no status filter), we might want to remove items that no longer fit
            // But usually for Manage Entries we just update status.

            setExperiments(prev => {
                return prev.map(exp =>
                    exp.recordId === id ? { ...exp, status: upperAction, reviewNote: note || null } : exp
                );
                // Note: For Moderation Queue, the parent might pass a specialized onAction that triggers a reload or we filter it out here?
                // Actually, if we just update status, the filter (if applicable) will hide it.
                // For Moderation Page where we only show PENDING, we should filter it out if status changes to APPROVED/REJECTED.
            });

            // If strict queue mode (e.g. Moderation), we might want to remove it manually if it wasn't filtered by status yet:
            // But let's rely on the useEffect filter or the parent's fetchData behavior on refresh.
            // For now, updating status is safe.

            if (!allowStatusFilter) {
                // Initial assumption: If no status filter, we might be in 'Pending Only' mode.
                // So if status changes, remove it?
                // Let's make it robust: just update status. The list filter will handle it if we are filtering by default?
                // Wait, `experiments` is the source. `filteredExperiments` is derived.
                // If `experiments` contains everything, `filteredExperiments` updates auto.
                // If `experiments` only contained PENDING (fetched from backend), and we change status, it stays in `experiments` but with new status.
                // If we want to remove it from view, we need to know if we are showing only pending.
            }

        } catch (err) {
            showError(`Failed to update status: ${err.message}`);
        }
    };

    const handleBulkActionWrapper = (newStatus) => {
        if (!onBulkAction) return;
        if (filteredExperiments.length === 0) return;
        // Guard against applying "All Visible" while background batches are
        // still loading - the visible set would be a partial one.
        if (!allLoaded) {
            showError('Please wait for all records to finish loading before running a bulk action.');
            return;
        }

        let displayAction = '';
        let expectedPhrase = '';

        switch (newStatus) {
            case 'APPROVED':
                displayAction = 'Approve All Visible';
                expectedPhrase = 'APPROVE ALL';
                break;
            case 'REJECTED':
                displayAction = 'Reject All Visible';
                expectedPhrase = 'REJECT ALL';
                break;
            case 'PENDING':
                displayAction = 'Set All Visible to Pending';
                expectedPhrase = 'PENDING ALL';
                break;
            case 'NEEDS_UPDATE':
                displayAction = 'Mark All Visible as Needs Update';
                expectedPhrase = 'NEEDS UPDATE ALL';
                break;
            default:
                return;
        }

        setConfirmModal({
            isOpen: true,
            action: newStatus,
            displayAction,
            expectedPhrase,
            count: filteredExperiments.length
        });
        setConfirmInput('');
    };

    const executeBulkAction = async () => {
        const { action, count } = confirmModal;
        const ids = filteredExperiments.map(exp => exp.recordId);

        try {
            await onBulkAction(ids, action);
            setExperiments(prev => prev.map(exp =>
                ids.includes(exp.recordId) ? { ...exp, status: action } : exp
            ));
            setConfirmModal({ ...confirmModal, isOpen: false });
            success(`Successfully updated ${count} entries to ${action}.`);
        } catch (err) {
            showError(`Failed to update statuses: ${err.message}`);
        }
    };

    const handleDeleteClick = (id) => {
        if (!allLoaded) {
            showError('Please wait for all records to finish loading before taking action.');
            return;
        }
        setDeleteModal({ isOpen: true, recordId: id });
        setDeleteConfirmInput('');
    };

    const executeDelete = async () => {
        const { recordId } = deleteModal;
        try {
            await onDelete(recordId);
            setExperiments(prev => prev.filter(exp => exp.recordId !== recordId));
            setDeleteModal({ isOpen: false, recordId: null });
            success(`Record #${recordId} was permanently deleted.`);
        } catch (err) {
            showError(`Failed to delete record: ${err.message}`);
        }
    };

    const StatusBadge = ({ status }) => {
        let badgeClass = 'bg-secondary';
        let label = status;
        if (status === 'APPROVED') badgeClass = 'bg-success';
        if (status === 'REJECTED') badgeClass = 'bg-danger';
        if (status === 'PENDING') badgeClass = 'bg-warning text-dark';
        if (status === 'NEEDS_UPDATE') {
            badgeClass = 'bg-info text-dark';
            label = 'Needs Update';
        }

        return <span className={`badge ${badgeClass}`}>{label}</span>;
    };


    return (
        <AdminLayout title={title} subtitle={subtitle}>
            {/* Stats Row */}
            <div className="dashboard-summary-row" style={{ marginBottom: '2rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                <div className="summary-card" style={{ flex: 1 }}>
                    <div className="summary-icon" style={{ backgroundColor: '#e3f2fd', color: '#1976d2' }}><i className="bi bi-collection"></i></div>
                    <div className="summary-info">
                        <span className="summary-label">Total</span>
                        <span className="summary-value">{stats.total}</span>
                    </div>
                </div>
                {/* Only show break down if it makes sense or if all stats are available */}
                <div className="summary-card" style={{ flex: 1 }}>
                    <div className="summary-icon" style={{ backgroundColor: '#fff8e1', color: '#f57f17' }}><i className="bi bi-hourglass-split"></i></div>
                    <div className="summary-info">
                        <span className="summary-label">Pending</span>
                        <span className="summary-value">{stats.pending}</span>
                    </div>
                </div>
                <div className="summary-card" style={{ flex: 1 }}>
                    <div className="summary-icon" style={{ backgroundColor: '#e8f5e9', color: '#2e7d32' }}><i className="bi bi-check-circle"></i></div>
                    <div className="summary-info">
                        <span className="summary-label">Approved</span>
                        <span className="summary-value">{stats.approved || 0}</span>
                    </div>
                </div>
                <div className="summary-card" style={{ flex: 1 }}>
                    <div className="summary-icon" style={{ backgroundColor: '#ffebee', color: '#c62828' }}><i className="bi bi-x-circle"></i></div>
                    <div className="summary-info">
                        <span className="summary-label">Rejected</span>
                        <span className="summary-value">{stats.rejected || 0}</span>
                    </div>
                </div>
                <div className="summary-card" style={{ flex: 1 }}>
                    <div className="summary-icon" style={{ backgroundColor: '#e1f5fe', color: '#0277bd' }}><i className="bi bi-exclamation-circle"></i></div>
                    <div className="summary-info">
                        <span className="summary-label">Needs Update</span>
                        <span className="summary-value">{stats.needsUpdate || 0}</span>
                    </div>
                </div>
            </div>

            <div className="admin-card">
                {!dataLoaded ? (
                    <div style={{ textAlign: 'center', padding: '3rem' }}>
                        <i className="bi bi-database" style={{ fontSize: '3rem', color: '#6c757d', marginBottom: '1rem', display: 'block' }}></i>
                        <h4 style={{ color: '#002244', marginBottom: '1rem' }}>{title}</h4>
                        <p className="text-muted" style={{ marginBottom: '2rem', maxWidth: '600px', margin: '0 auto 2rem auto' }}>
                            Load the entries to view and manage.
                        </p>
                        <button className="btn btn-primary btn-lg" onClick={loadExperiments} disabled={loading}>
                            {loading ? (
                                <>
                                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                    Loading...
                                </>
                            ) : (
                                <>
                                    <i className="bi bi-cloud-download me-2"></i> Load Entries
                                </>
                            )}
                        </button>
                    </div>
                ) : (
                    <>
                        {/* Controls */}
                        <div style={{ marginBottom: '1.5rem', display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
                            <div style={{ position: 'relative', flex: 1, minWidth: '250px' }}>
                                <i className="bi bi-search" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#6c757d' }}></i>
                                <input
                                    type="text"
                                    placeholder="Search by polymer, email, name, or ID..."
                                    className="form-control"
                                    style={{ paddingLeft: '40px' }}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            <select className="form-select" style={{ maxWidth: '14rem' }} value={sortOrder}
                                onChange={(e) => setSortOrder(e.target.value)} aria-label="Sort entries">
                                <option value="newest">Newest first</option>
                                <option value="completeness-asc">Least complete first</option>
                                <option value="completeness-desc">Most complete first</option>
                            </select>

                            {allowStatusFilter && (
                                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                    <label><strong>Status:</strong></label>
                                    <select
                                        value={filterStatus}
                                        onChange={(e) => setFilterStatus(e.target.value)}
                                        className="form-control"
                                        style={{ width: '150px' }}
                                    >
                                        <option value="ALL">All Status</option>
                                        {statusFilterOptions.map(status => (
                                            <option key={status} value={status}>{STATUS_FILTER_LABELS[status]}</option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <button className="btn btn-outline-primary" onClick={loadExperiments}>
                                <i className="bi bi-arrow-clockwise"></i> Refresh
                            </button>

                            {!allLoaded && loadProgress.total > 0 && (
                                <span className="text-muted small" style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    Loading {loadProgress.loaded} of {loadProgress.total}...
                                </span>
                            )}

                            {allowBulkActions && (
                                <div className="btn-group ms-2" title={!allLoaded ? 'Waiting for all records to finish loading...' : undefined}>
                                    {allowedActions.includes('APPROVED') && (
                                        <button className="btn btn-outline-success" onClick={() => handleBulkActionWrapper('APPROVED')} disabled={filteredExperiments.length === 0 || !allLoaded} title="Accept All Visible">
                                            <i className="bi bi-check-all"></i>
                                        </button>
                                    )}
                                    {allowedActions.includes('REJECTED') && (
                                        <button className="btn btn-outline-danger" onClick={() => handleBulkActionWrapper('REJECTED')} disabled={filteredExperiments.length === 0 || !allLoaded} title="Reject All Visible">
                                            <i className="bi bi-x-circle"></i>
                                        </button>
                                    )}
                                    {allowedActions.includes('PENDING') && (
                                        <button className="btn btn-outline-warning" onClick={() => handleBulkActionWrapper('PENDING')} disabled={filteredExperiments.length === 0 || !allLoaded} title="Pending All Visible">
                                            <i className="bi bi-pause-circle"></i>
                                        </button>
                                    )}
                                    {allowedActions.includes('NEEDS_UPDATE') && (
                                        <button className="btn btn-outline-info" onClick={() => handleBulkActionWrapper('NEEDS_UPDATE')} disabled={filteredExperiments.length === 0 || !allLoaded} title="Mark All Visible as Needs Update">
                                            <i className="bi bi-exclamation-circle"></i>
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        {loading && <p className="text-center my-4">Loading entries...</p>}
                        {error && <div className="alert alert-danger">{error}</div>}

                        {!loading && !error && filteredExperiments.length === 0 && (
                            <p className="text-muted text-center my-4">{emptyMessage}</p>
                        )}

                        {!loading && !error && filteredExperiments.length > 0 && (
                            <div className="table-responsive">
                                <table className="admin-table">
                                    <thead>
                                        <tr>
                                            <th>ID</th>
                                            <th>Date</th>
                                            <th>Submitter</th>
                                            <th>Polymer / Solvent</th>
                                            <th>Images</th>
                                            <th title="Share of the tracked fields that are filled (required fields count double)">Complete</th>
                                            <th>Details</th>
                                            <th>Status</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredExperiments.map((exp) => (
                                            <React.Fragment key={exp.recordId}>
                                                <tr>
                                                    <td><span className="fw-bold fw-mono">{exp.recordId}</span></td>
                                                    <td>
                                                        {new Date(exp.timestamp).toLocaleDateString()}
                                                        <br />
                                                        <small className="text-muted">{new Date(exp.timestamp).toLocaleTimeString()}</small>
                                                    </td>
                                                    <td>
                                                        {exp.userInfo ? (
                                                            <div>
                                                                <strong>{exp.userInfo.name}</strong><br />
                                                                <small className="text-muted">{exp.userInfo.email}</small>
                                                            </div>
                                                        ) : <span className="text-muted">Unknown</span>}
                                                    </td>
                                                    <td>
                                                        <div style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            <strong>{exp.polymerProperty?.polymerComponents?.map(c => c.polymerName).join(', ') || 'N/A'}</strong><br />
                                                            <small className="text-muted">
                                                                {exp.solventProperty?.solventComponents?.map(c => c.solventName).join(', ') || 'N/A'}
                                                            </small>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <ReviewImage images={exp.fiberImages || []} compact={true} />
                                                    </td>
                                                    <td>
                                                        {exp.completenessScore != null ? (
                                                            <span className="fw-semibold" style={{ color: exp.completenessScore >= 75 ? '#15803d' : exp.completenessScore >= 50 ? '#b45309' : '#b91c1c' }}>
                                                                {exp.completenessScore}%
                                                            </span>
                                                        ) : <span className="text-muted">–</span>}
                                                    </td>
                                                    <td>
                                                        <button
                                                            className="btn-icon details"
                                                            onClick={() => handleToggle(exp.recordId)}
                                                        >
                                                            <i className={`bi bi-${expandedId === exp.recordId ? 'eye-slash' : 'eye'}`}></i>
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <StatusBadge status={exp.status} />
                                                        {exp.openIssues?.length > 0 && (
                                                            <button
                                                                type="button"
                                                                className="btn btn-link btn-sm p-0 d-block text-danger text-decoration-none small mt-1"
                                                                onClick={() => setExpandedId(exp.recordId)}
                                                                title={exp.openIssues.join('\n')}
                                                            >
                                                                <i className="bi bi-exclamation-triangle me-1" aria-hidden="true"></i>
                                                                {exp.openIssues.length} to fix
                                                            </button>
                                                        )}
                                                    </td>
                                                    <td>
                                                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                            {allowedActions.includes('APPROVED') && exp.status !== 'APPROVED' && (
                                                                <button onClick={() => handleAction(exp.recordId, 'APPROVED')} className="btn btn-sm btn-outline-success" disabled={!allLoaded} title={allLoaded ? 'Approve' : 'Waiting for all records to finish loading...'}>
                                                                    <i className="bi bi-check-lg"></i>
                                                                </button>
                                                            )}
                                                            {allowedActions.includes('REJECTED') && exp.status !== 'REJECTED' && (
                                                                <button onClick={() => handleAction(exp.recordId, 'REJECTED')} className="btn btn-sm btn-outline-danger" disabled={!allLoaded} title={allLoaded ? 'Reject' : 'Waiting for all records to finish loading...'}>
                                                                    <i className="bi bi-x-lg"></i>
                                                                </button>
                                                            )}
                                                            {allowedActions.includes('PENDING') && exp.status !== 'PENDING' && (
                                                                <button onClick={() => handleAction(exp.recordId, 'PENDING')} className="btn btn-sm btn-outline-warning" disabled={!allLoaded} title={allLoaded ? 'Set Pending' : 'Waiting for all records to finish loading...'}>
                                                                    <i className="bi bi-pause-fill"></i>
                                                                </button>
                                                            )}
                                                            {allowedActions.includes('NEEDS_UPDATE') && exp.status !== 'NEEDS_UPDATE' && (
                                                                <button onClick={() => handleAction(exp.recordId, 'NEEDS_UPDATE')} className="btn btn-sm btn-outline-info" disabled={!allLoaded} title={allLoaded ? 'Mark Needs Update' : 'Waiting for all records to finish loading...'}>
                                                                    <i className="bi bi-exclamation-circle"></i>
                                                                </button>
                                                            )}
                                                            {allowDelete && (
                                                                <button onClick={() => handleDeleteClick(exp.recordId)} className="btn btn-sm btn-danger" disabled={!allLoaded} title={allLoaded ? 'Permanently Delete' : 'Waiting for all records to finish loading...'}>
                                                                    <i className="bi bi-trash3"></i>
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                                {expandedId === exp.recordId && (
                                                    <tr>
                                                        <td colSpan="9" style={{ padding: '0 1rem 1rem 1rem', borderTop: 'none', backgroundColor: '#f8f9fa' }}>
                                                            <div className="detail-view" style={{ background: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e9ecef', marginTop: '0.5rem' }}>
                                                                <h5 style={{ marginBottom: '1.5rem', color: '#002244', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                                                    <i className="bi bi-info-circle"></i> Record Details
                                                                </h5>
                                                                <RecordDetailView experiment={exp} />
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Bulk Action Modal */}
            {confirmModal.isOpen && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1050,
                    backdropFilter: 'blur(3px)'
                }}>
                    <div style={{
                        backgroundColor: 'white', padding: '2rem', borderRadius: '12px',
                        width: '500px', maxWidth: '90%', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
                        textAlign: 'center'
                    }}>
                        <div style={{ marginBottom: '1.5rem' }}>
                            <i className="bi bi-exclamation-triangle text-warning" style={{ fontSize: '3rem' }}></i>
                        </div>
                        <h4 style={{ color: '#002244', marginBottom: '1rem' }}>Confirm Bulk Action</h4>
                        <p className="mb-3">
                            You are about to <strong>{confirmModal.displayAction.toLowerCase()}</strong> for <strong>{confirmModal.count}</strong> entries.
                        </p>
                        <p className="text-muted small mb-4">
                            Type <strong>{confirmModal.expectedPhrase}</strong> to confirm.
                        </p>
                        <input
                            type="text"
                            className="form-control mb-4 text-center"
                            placeholder={confirmModal.expectedPhrase}
                            value={confirmInput}
                            onChange={(e) => setConfirmInput(e.target.value)}
                            style={{ fontWeight: 'bold', letterSpacing: '1px' }}
                        />
                        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                            <button className="btn btn-outline-secondary" onClick={() => setConfirmModal({ ...confirmModal, isOpen: false })}>
                                Cancel
                            </button>
                            <button
                                className="btn btn-danger"
                                onClick={executeBulkAction}
                                disabled={confirmInput !== confirmModal.expectedPhrase}
                            >
                                Confirm Action
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Note to the contributor */}
            {noteModal && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1050,
                    backdropFilter: 'blur(3px)'
                }} role="dialog" aria-modal="true" aria-labelledby="note-modal-title">
                    <div style={{
                        backgroundColor: 'white', padding: '1.75rem', borderRadius: '12px',
                        width: '560px', maxWidth: '92%', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)'
                    }}>
                        <h4 id="note-modal-title" style={{ color: '#002244', marginBottom: '0.5rem' }}>
                            {noteModal.action === 'REJECTED' ? 'Reject' : 'Ask for an update to'} record #{noteModal.id}
                        </h4>
                        <p className="text-muted small mb-3">
                            The contributor sees this note next to the run in <em>My contributions</em> and when editing it.
                            {noteModal.action === 'REJECTED' ? ' Say why, so they can correct and resubmit it.' : ' Say what to add or correct.'}
                        </p>
                        <label htmlFor="review-note" className="form-label fw-semibold">Note to the contributor</label>
                        <textarea id="review-note" className="form-control mb-1" rows={4} maxLength={2000} autoFocus
                            placeholder={noteModal.action === 'REJECTED'
                                ? 'e.g. The values at 18 kV are not in the paper (Table 2 lists 12, 15 and 21 kV).'
                                : 'e.g. Please add the relative humidity; Section 2.3 gives it.'}
                            value={noteModal.text} onChange={(e) => setNoteModal({ ...noteModal, text: e.target.value })} />
                        <div className="text-muted small text-end mb-3">{noteModal.text.length}/2000</div>
                        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                            <button className="btn btn-outline-secondary" onClick={() => setNoteModal(null)}>Cancel</button>
                            <button className={`btn ${noteModal.action === 'REJECTED' ? 'btn-danger' : 'btn-primary'}`}
                                onClick={() => { const { id, action, text } = noteModal; setNoteModal(null); handleAction(id, action, text.trim()); }}>
                                {noteModal.action === 'REJECTED' ? 'Reject' : 'Send back'}{noteModal.text.trim() ? ' with note' : ' without note'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Permanent Delete Modal */}
            {deleteModal.isOpen && (
                <div style={{
                    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', zIndex: 1050,
                    backdropFilter: 'blur(3px)'
                }}>
                    <div style={{
                        backgroundColor: 'white', padding: '2rem', borderRadius: '12px',
                        width: '500px', maxWidth: '90%', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.2)',
                        textAlign: 'center'
                    }}>
                        <div style={{ marginBottom: '1.5rem' }}>
                            <i className="bi bi-trash3 text-danger" style={{ fontSize: '3rem' }}></i>
                        </div>
                        <h4 style={{ color: '#002244', marginBottom: '1rem' }}>Permanently Delete Record</h4>
                        <p className="mb-3">
                            You are about to <strong>permanently delete record #{deleteModal.recordId}</strong> and all of its associated data and images.
                            This action cannot be undone and leaves no trace.
                        </p>
                        <p className="text-muted small mb-4">
                            Type <strong>DELETE {deleteModal.recordId}</strong> to confirm.
                        </p>
                        <input
                            type="text"
                            className="form-control mb-4 text-center"
                            placeholder={`DELETE ${deleteModal.recordId}`}
                            value={deleteConfirmInput}
                            onChange={(e) => setDeleteConfirmInput(e.target.value)}
                            style={{ fontWeight: 'bold', letterSpacing: '1px' }}
                        />
                        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                            <button className="btn btn-outline-secondary" onClick={() => setDeleteModal({ isOpen: false, recordId: null })}>
                                Cancel
                            </button>
                            <button
                                className="btn btn-danger"
                                onClick={executeDelete}
                                disabled={deleteConfirmInput !== `DELETE ${deleteModal.recordId}`}
                            >
                                Permanently Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
    );
};

export default SubmissionManager;
